import { useState } from "react";
import { BookmarkPlus, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const AddToWatchlist = ({ ticker, compact = false }) => {
  const [lists, setLists] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/watchlists").then((r) => setLists(r.data.watchlists)).catch(() => {});

  const add = async (wid, wname) => {
    try {
      const { data } = await api.post(`/watchlists/${wid}/tickers`, { ticker });
      toast.success(`${ticker} → ${wname}`, { description: data.message });
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  const createAndAdd = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/watchlists", { name: name.trim(), tickers: [ticker] });
      toast.success(`Created "${name.trim()}" with ${ticker}`);
      setDialogOpen(false);
      setName("");
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DropdownMenu onOpenChange={(o) => o && load()}>
        <DropdownMenuTrigger asChild>
          {compact ? (
            <button
              data-testid={`watchlist-add-${ticker}`}
              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-white/5 rounded-sm transition-colors duration-150"
              title={`Add ${ticker} to watchlist`}
            >
              <BookmarkPlus className="w-4 h-4" />
            </button>
          ) : (
            <Button
              variant="outline"
              data-testid={`watchlist-add-${ticker}`}
              className="rounded-sm border-border text-muted-foreground hover:text-white gap-2"
            >
              <BookmarkPlus className="w-4 h-4" /> Watchlist
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 rounded-sm">
          <DropdownMenuLabel className="overline-label">Add {ticker} to</DropdownMenuLabel>
          {lists.length === 0 && (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              No watchlists yet
            </DropdownMenuItem>
          )}
          {lists.map((w) => (
            <DropdownMenuItem
              key={w.id}
              data-testid={`watchlist-option-${w.id}`}
              className="cursor-pointer text-sm"
              onClick={() => add(w.id, w.name)}
            >
              {w.name}
              <span className="ml-auto font-mono text-xs text-muted-foreground">{w.tickers.length}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="watchlist-new-option"
            className="cursor-pointer text-sm text-primary focus:text-primary"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" /> New watchlist…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm bg-card border-border sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">New watchlist</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Name your watchlist — {ticker} will be added to it.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="e.g. Dividend ideas"
            className="rounded-sm bg-background"
            data-testid="new-watchlist-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createAndAdd()}
          />
          <Button
            onClick={createAndAdd}
            disabled={busy || !name.trim()}
            className="rounded-sm bg-primary hover:bg-primary/85 text-white"
            data-testid="create-watchlist-confirm-button"
          >
            Create &amp; add {ticker}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
